import { useMemo, useState } from "react"
import { IconUsersPlus } from "@tabler/icons-react"
import { useAppDispatch, useAppSelector } from "@/store"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { hasPermission } from "@/store/permissionsSlice"
import {
  navDay,
  goToday,
  setSelectedTeam,
  viewStaffWeek,
  openBooking,
  closeBooking,
  openOverlap,
  closeOverlap,
  openSlotBooking,
  closeSlotBooking,
} from "@/app/booking-calendar/bookingCalendarSlice"
import { findBookingInColumn, headCell, overlapGroup } from "../lib/dayGrid"
import { SITE_TIME_ZONE, longDateLabel } from "../lib/siteTime"
import { usePacificToday } from "../lib/usePacificToday"
import { useTeamDay } from "../lib/useTeamDay"
import { bookableSlotTypes, slotTypeOf } from "../lib/slotTypes"
import { DayView } from "../components/DayView"
import { AppointmentDialog } from "../components/modals"
import { NewBookingDialog } from "../components/NewBookingDialog"
import { OverlapDialog } from "../components/OverlapDialog"
import { TeamMembershipDialog } from "../components/TeamMembershipDialog"
import { ScheduleToolbar, ScheduleLegend, ScheduleLoadingSkeleton } from "../components/ScheduleHeader"
import { BookingSeasonBanner } from "../components/BookingSeasonBanner"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { useGetBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi"

export default function TeamDayPage() {
  const dispatch = useAppDispatch()
  const { selectedDate, selectedTeamId, openBookingUid, overlapBookingUids, newBookingTarget } =
    useAppSelector(state => state.bookingCalendar)
  const { permissions } = useAppSelector(state => state.permissions)

  // Local rather than in the slice: nothing outside this sidebar reads it, and unlike the open
  // appointment it is not part of what the user is looking at.
  const [editingMembers, setEditingMembers] = useState(false)

  const canManage = hasPermission(permissions, "appointments:manage")

  // When the public may book, for the grid to mark the times they may not. Every calendar user can
  // read it. Undefined on a failed read as well as while loading: the calendar then just says
  // nothing about the season — staff are never held to it, so it is never worth blocking on.
  const seasonQuery = useGetBookingSeasonQuery()
  const bookingSeason = seasonQuery.isError ? undefined : seasonQuery.data

  const day = useTeamDay(selectedDate, selectedTeamId, bookingSeason)
  const {
    teams, team, columns, schedule, slotMinutes, status, slotsLoading, error, unavailableReason,
    columnUnavailability, availabilityUnavailable,
  } = day

  // The zone the team's appointments are drawn in. Not the zone "Today" is in: that is the office's,
  // Pacific, whatever Cal has the team in (AB#821) — a team set to a later zone marked tomorrow as
  // today from mid-afternoon on. Kept current past midnight by `usePacificToday`.
  const timeZone = team?.timeZone || SITE_TIME_ZONE
  const today = usePacificToday()

  // The open appointment, found by walking the grid rather than kept in the store: the grid is the
  // only place a booking and the member hosting it are already joined.
  // Includes an appointment that lost its row to another, which is reachable only from the overlap list.
  const open = useMemo(() => {
    if (!openBookingUid) return undefined
    for (const column of columns) {
      const booking = findBookingInColumn(schedule[column.id], openBookingUid)
      if (booking) return { booking, staffName: column.name }
    }
    return undefined
  }, [openBookingUid, columns, schedule])

  // The overlap list, resolved against the current grid. One cancelled from the list has left the
  // grid, so it leaves the list too. Hidden while one of them is open on top of it.
  const overlapBookings = useMemo(() => {
    if (!overlapBookingUids || openBookingUid) return undefined
    return overlapBookingUids.flatMap(uid => {
      for (const column of columns) {
        const booking = findBookingInColumn(schedule[column.id], uid)
        if (booking) return [booking]
      }
      return []
    })
  }, [overlapBookingUids, openBookingUid, columns, schedule])

  // Booking into a free slot needs a booking permission, and which rows it opens turns on which
  // (AB#779) — only somebody with appointments:manage reaches this page, so in practice all three.
  // A team Cal never slugged cannot be addressed by the booking endpoint at all, so there is
  // nothing to offer for one.
  const bookableTypes = useMemo(() => bookableSlotTypes(permissions), [permissions])
  const canBookSlots = bookableTypes.length > 0 && !!team?.slug

  // The slot being booked into, widened with the things the dialog needs and the target deliberately
  // does not carry: the host's display name, and the site's slug.
  const newBooking = useMemo(() => {
    if (!newBookingTarget || !team?.slug) return undefined
    const column = columns.find(c => c.id === newBookingTarget.columnId)
    if (!column) return undefined
    return { target: newBookingTarget, hostName: column.name, teamSlug: team.slug }
  }, [newBookingTarget, columns, team?.slug])

  return (
    <div className="booking-calendar-layout">
      {/* ── Sidebar: teams, then this team's members ─────────────── */}
      <aside className="booking-calendar-sidebar">
        <div className="px-3 pt-3.5 pb-1.5">
          <label
            htmlFor="team-select"
            className="block px-2 mb-1.5 text-[0.63rem] font-bold tracking-[0.1em] uppercase text-muted-foreground"
          >
            Team
          </label>
          {teams.length === 0 && status !== "loading" ? (
            <p className="px-2.5 text-[0.7rem] text-muted-foreground">
              No teams are set up for appointments yet.
            </p>
          ) : (
            <Select
              value={team ? String(team.id) : undefined}
              disabled={teams.length === 0}
              onValueChange={value => dispatch(setSelectedTeam(Number(value)))}
            >
              <SelectTrigger id="team-select" className="w-full">
                <SelectValue placeholder="Choose a team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map(t => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                    {/* A team with no slug was never provisioned in Cal and can take no bookings. */}
                    {!t.slug && <span className="text-muted-foreground">(not set up)</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="px-3 pt-1 pb-1.5">
          <div className="flex items-center gap-1 px-2 mb-1.5">
            <p className="flex-1 text-[0.63rem] font-bold tracking-[0.1em] uppercase text-muted-foreground">
              Team Members
            </p>
            {/* Managing membership is a write, so it needs appointments:manage — viewers get the
                roster without a button that would only 403 on them. A team Cal never slugged cannot
                be addressed by the assignment endpoint, so there is nothing to open for it. */}
            {canManage && team && (
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Manage team members"
                title={team.slug ? "Manage team members" : "This team isn't set up for appointments yet"}
                disabled={!team.slug}
                onClick={() => setEditingMembers(true)}
              >
                <IconUsersPlus className="size-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="px-3 pb-4">
          {/* Not while the roster is still on its way: a team being switched to has no columns yet,
              and saying it has no members would be the one answer nobody has had. */}
          {columns.length === 0 && status !== "loading" && (
            <p className="px-2.5 text-[0.7rem] text-muted-foreground">
              {unavailableReason ?? "This team has no members yet."}
            </p>
          )}
          {columns.map(member => {
            // Continuation rows belong to the appointment above them, so counting them would double
            // every booking that spans more than one row.
            const booked = schedule[member.id]?.filter(s => s.state === "booked" && !s.continuation).length ?? 0
            const away = columnUnavailability[member.id]
            return (
              // A column on the grid is one day of one person; the name beside it opens their week.
              // "Is Tuesday covered" is a question about the office, which the grid answers, and
              // "what does this week look like for them" is a question about the person, which it
              // cannot — the day it draws is one column of five.
              <button
                key={member.id}
                type="button"
                title={`Open ${member.name}'s week`}
                className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md mb-0.5 transition-colors hover:bg-muted"
                onClick={() => dispatch(viewStaffWeek({
                  calUserId: member.calUserId,

                  // The office they were looking at, so the week opens there rather than merged
                  // across every site this person works at.
                  siteId: team?.id ?? null,
                }))}
              >
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: member.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{member.name}</div>
                  {/* Without this, someone away all day is a name with a 0 beside it — which reads
                      as "free all day", the opposite of what it means. */}
                  {away && (
                    <div className="text-[0.65rem] text-muted-foreground truncate">{away.label}</div>
                  )}
                </div>
                {/* No count until the day is in: a 0 here would say "free all day" about a day nobody
                    has read yet. */}
                {slotsLoading
                  ? <div className="slot-shimmer h-5 w-6 rounded-full" aria-hidden />
                  : <Badge variant="secondary" className="h-5 px-1.5 text-[0.68rem] rounded-full">{booked}</Badge>}
              </button>
            )
          })}
        </div>
      </aside>

      {/* ── Main content: the day ────────────────────────────────── */}
      <main className="booking-calendar-main">
        {/* The toolbar and the key pin together — see .booking-calendar-header. */}
        <div className="booking-calendar-header">
          <ScheduleToolbar
            onNavDay={(delta) => dispatch(navDay(delta))}
            onGoToday={() => dispatch(goToday())}
            onRefresh={day.refresh}
            refreshing={day.isFetching}
            loading={slotsLoading}
            teamLabel={team?.name ?? "No team"}
            dayLabel={longDateLabel(selectedDate)}
            isToday={selectedDate === today}
            bookingCount={day.bookingCount}
            outsideWindowCount={day.outsideWindowCount}
            unhostedCount={day.unhostedCount}
            awayCount={Object.keys(columnUnavailability).length}
            slotMinutes={slotMinutes}
          />
          <ScheduleLegend bookable={canBookSlots} bookingSeason={!!bookingSeason} />
          <BookingSeasonBanner season={bookingSeason} />
        </div>

        {/* Cal throttling CMS is a wait, not a failure (AB#514): the busy notice, with one retry of
            its own once the wait is over, in place of the generic line. */}
        {day.throttledError ? (
          <CalThrottledNotice
            error={day.throttledError}
            onRetry={day.refresh}
            busy={day.isFetching}
            autoRetry
            className="px-5 py-2 text-xs"
          />
        ) : status === "failed" && (
          <p role="alert" className="px-5 py-2 text-xs text-red-700">{error}</p>
        )}

        {unavailableReason && status !== "loading" && (
          <p className="px-5 py-2 text-xs text-muted-foreground">{unavailableReason}</p>
        )}

        {/* The grid is now over-stating availability, and silence about it would be the worst of
            both worlds: a calendar that looks authoritative and is not. */}
        {availabilityUnavailable && status !== "loading" && (
          <p role="alert" className="px-5 py-2 text-xs text-amber-700">
            Staff availability could not be loaded, so slots shown as open here may not be bookable.
          </p>
        )}

        {status === "loading"
          ? <ScheduleLoadingSkeleton />
          : (
            <DayView
              schedule={schedule}
              columns={columns}
              columnUnavailability={columnUnavailability}
              bookable={canBookSlots}
              bookableSlotTypes={bookableTypes}
              loading={slotsLoading}
              onSlotClick={(staffId, slotIdx) => {
                const cell = schedule[staffId]?.[slotIdx]
                if (!cell) return

                // A row with an appointment on it opens that appointment, whether it is the head row
                // or one of the rows a longer booking covers — unless it collides with others, when
                // it lists them all to pick from.
                if (cell.slotId) {
                  const head = headCell(schedule[staffId], cell.slotId)
                  if (head?.hasOverlap) dispatch(openOverlap(overlapGroup(head).map(b => b.uid)))
                  else dispatch(openBooking(cell.slotId))
                  return
                }

                // A free row offers to be booked into. Unavailable rows reach neither branch: they
                // are not clickable, because an open slot is an invitation and they are the opposite.
                const slotType = slotTypeOf(cell)
                if (cell.state !== "open" || !canBookSlots || !bookableTypes.includes(slotType)) return

                const column = columns.find(c => c.id === staffId)
                if (!column) return

                dispatch(openSlotBooking({
                  columnId: staffId,
                  calUserId: column.calUserId,
                  date: selectedDate,
                  startMinutes: cell.startMinutes,
                  // A free row is free for one kind of appointment, and this is what says which.
                  slotType,
                }))
              }}
              onColumnClick={(column) =>
                dispatch(viewStaffWeek({ calUserId: column.calUserId, siteId: team?.id ?? null }))}
            />
          )}
      </main>

      <AppointmentDialog
        booking={open?.booking}
        staffName={open?.staffName}
        timeZone={timeZone}
        onClose={() => dispatch(closeBooking())}
      />

      <OverlapDialog
        bookings={overlapBookings}
        timeZone={timeZone}
        onPick={uid => dispatch(openBooking(uid))}
        onClose={() => dispatch(closeOverlap())}
      />

      <NewBookingDialog
        target={newBooking?.target}
        hostName={newBooking?.hostName}
        teamSlug={newBooking?.teamSlug}
        timeZone={timeZone}
        slotMinutes={slotMinutes}
        bookingSeason={bookingSeason}
        onClose={() => dispatch(closeSlotBooking())}
      />

      {team && (
        <TeamMembershipDialog
          team={team}
          open={editingMembers}
          onOpenChange={setEditingMembers}
        />
      )}
    </div>
  )
}
